/**
 * Product Kit Builder
 * Gerencia a interface de montagem de kits personalizáveis
 */

class ProductKitBuilder {
  constructor(container, config) {
    this.container = container;
    this.config = config;
    this.currentSlide = 0;
    this.selections = new Map(); // key: `${productId}-${rep}` -> { productId, variantId }
    
    // Elementos DOM
    this.slider = container.querySelector('[data-kit-slider]');
    this.slides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    this.prevButton = container.querySelector('[data-kit-prev]');
    this.nextButton = container.querySelector('[data-kit-next]');
    this.addButton = container.querySelector('[data-kit-add]');
    this.currentCounter = container.querySelector('[data-kit-current]');
    this.totalCounter = container.querySelector('[data-kit-total]');
    
    this.init();
  }

  init() {
    // Inicializar seleções para todas as instâncias de slides
    const allSlides = Array.from(this.container.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const productData = this.config.products.find((p) => p.id === productId);
      const key = `${productId}-${rep}`;
      if (productData) {
        this.selections.set(key, {
          productId,
          variantId: productData.firstAvailableVariantId
        });
      }
    });

    // Aplicar tamanho de kit padrão (primeiro valor do CSV)
    const defaultSize =
      this.config.kitDefaultSize ||
      (this.config.kitSizes && this.config.kitSizes.length > 0
        ? parseInt(this.config.kitSizes[0], 10)
        : 2);
    this.applyKitSize(defaultSize);

    this.setupEventListeners();
    this.showSlide(0);
  }

  setupEventListeners() {
    // Botões de navegação
    this.prevButton.addEventListener('click', () => this.previousSlide());
    this.nextButton.addEventListener('click', () => this.nextSlide());
    this.addButton.addEventListener('click', () => this.addToCart());

    // Radio buttons de variantes (delegação)
    this.container.addEventListener('change', (e) => {
      const radio = e.target.closest('.kit-variant-radio');
      if (radio) this.handleVariantChange(e);
    });

    // Navegação por teclado
    this.container.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Seletor de tamanhos do kit
    const sizeButtons = this.container.querySelectorAll('[data-kit-size]');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const size = parseInt(btn.dataset.kitSize);
        this.applyKitSize(size);

        // Atualiza estados visuais dos botões
        sizeButtons.forEach((b) => {
          const pressed = parseInt(b.dataset.kitSize) === size;
          b.setAttribute('aria-pressed', pressed ? 'true' : 'false');
          b.classList.toggle('is-active', pressed);
        });

        // Subtextos permanecem sempre visíveis por opção
      });
    });
  }

  getVisibleSlides() {
    return Array.from(this.slider.querySelectorAll('.kit-product-slide:not([data-hidden="true"])'));
  }

  applyKitSize(size) {
    // Oculta/mostra slides por repetição
    const allSlides = Array.from(this.slider.querySelectorAll('.kit-product-slide'));
    allSlides.forEach((slide) => {
      const rep = parseInt(slide.dataset.rep || '1');
      if (rep > size) {
        slide.style.display = 'none';
        slide.setAttribute('data-hidden', 'true');
      } else {
        slide.style.display = '';
        slide.removeAttribute('data-hidden');
      }
    });

    // Atualiza lista interna e contador
    this.slides = this.getVisibleSlides();
    if (this.totalCounter) {
      this.totalCounter.textContent = this.slides.length;
    }
    this.currentSlide = 0;
    this.showSlide(0);

    // Atualiza desconto por tamanho conforme modo configurado (percent | fixed)
    const configuredMode = this.config.discountMode || 'percent';
    const discountPercent = (this.config.kitDiscounts && this.config.kitDiscounts[String(size)]) || 0;
    const discountFixed = (this.config.kitDiscountsFixed && this.config.kitDiscountsFixed[String(size)]) || 0;

    if (configuredMode === 'fixed' && discountFixed > 0) {
      this.config.discountMultiplier = 1.0;
      this.config.discountFixedCents = discountFixed;
      this.config.discountType = 'fixed';
    } else if (configuredMode === 'percent' && discountPercent > 0) {
      this.config.discountMultiplier = (100 - discountPercent) / 100;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'percent';
    } else {
      this.config.discountMultiplier = 1;
      this.config.discountFixedCents = 0;
      this.config.discountType = 'none';
    }

    // Recalcular preços dos slides visíveis
    const visibleSlides = this.getVisibleSlides();
    this.slides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      const productData = this.config.products.find((p) => p.id === productId);
      let variant = undefined;
      if (selection) {
        variant = productData?.variants.find((v) => v.id === selection.variantId) || productData?.variants[0];
      } else {
        variant = productData?.variants[0];
      }
      if (variant) {
        this.updateProductPrice(slide, variant, visibleSlides.length);
      }
    });
  }

  handleVariantChange(event) {
    const radio = event.target;
    if (!radio.checked) return;

    const slide = radio.closest('.kit-product-slide');
    const productIndex = parseInt(slide.dataset.productIndex) - 1;
    const productId = parseInt(slide.dataset.productId);
    const rep = parseInt(slide.dataset.rep || '1');
    const fieldset = radio.closest('.kit-variant-fieldset');
    
    // Atualizar valor atual exibido
    const currentValueSpan = fieldset.querySelector('.current-value');
    if (currentValueSpan) {
      currentValueSpan.textContent = radio.value;
      currentValueSpan.dataset.currentValue = radio.value;
    }

    // Obter todas as opções selecionadas
    const selectedOptions = Array.from(
      slide.querySelectorAll('.current-value')
    ).map((el) => el.dataset.currentValue);

    console.log('🔍 Opções selecionadas:', selectedOptions);

    // Encontrar produto e variante correspondente
    const product = this.config.products[productIndex];
    const variant = product.variants.find((v) => {
      return JSON.stringify(v.options) === JSON.stringify(selectedOptions);
    });

    if (variant) {
      console.log('✅ Variante encontrada:', variant);
      
      // Atualizar seleção específica desta instância
      const key = `${productId}-${rep}`;
      this.selections.set(key, {
        productId,
        variantId: variant.id
      });

      // Atualizar imagem
      this.updateProductImage(slide, variant);

      // Atualizar preço
      const visibleSlides = this.getVisibleSlides();
      this.updateProductPrice(slide, variant, visibleSlides.length);

      // Atualizar disponibilidade dos botões
      this.updateNavigationAvailability(slide, variant);

      // Atualizar estado dos swatches
      this.updateSwatchAvailability(slide, product, selectedOptions);
    } else {
      console.error('❌ Variante não encontrada para:', selectedOptions);
    }
  }

  updateProductImage(slide, variant) {
    const image = slide.querySelector('[data-kit-image]');
    if (!image) return;

    if (variant.featured_image) {
      image.src = variant.featured_image;
      image.alt = variant.options.join(' - ');
    }

    const slideProductId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === slideProductId);
    if (!productData) return;

    // Preferir opção cujo fieldset é de cor; fallback options[0]
    let colorValue = '';
    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    fieldsets.forEach((fs) => {
      const legend = fs.querySelector('.kit-variant-legend');
      const name = (legend?.childNodes?.[0]?.textContent || legend?.textContent || '').toLowerCase();
      if (name.includes('cor') || name.includes('color')) {
        colorValue = fs.querySelector('.current-value')?.dataset?.currentValue || '';
      }
    });
    if (!colorValue) {
      colorValue = variant.options[0] || '';
    }

    this.updateGalleryWithProductImages(productData.handle, colorValue);
  }

  updateProductPrice(slide, variant, totalProducts = 1) {
    const priceElement = slide.querySelector('[data-kit-price]');
    if (!priceElement) return;

    let discountedPrice = variant.price;
    let hasDiscount = false;

    // Aplicar desconto conforme tipo
    if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo: dividir proporcionalmente por produto
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    } else if (this.config.discountType === 'percent' && this.config.discountMultiplier < 1) {
      // Desconto por porcentagem
      discountedPrice = variant.price * this.config.discountMultiplier;
      hasDiscount = true;
    } else if (this.config.discountType === 'fixed' && this.config.discountFixedCents > 0) {
      // Desconto fixo geral
      const discountPerProduct = Math.floor(this.config.discountFixedCents / totalProducts);
      discountedPrice = Math.max(0, variant.price - discountPerProduct);
      hasDiscount = discountPerProduct > 0;
    }

    priceElement.textContent = this.formatMoney(discountedPrice);

    // Mostrar preço cortado se houver desconto
    const compareElement = slide.querySelector('[data-kit-compare-price]');
    if (compareElement) {
      if (hasDiscount) {
        compareElement.textContent = this.formatMoney(variant.price);
        compareElement.style.display = '';
      } else {
        compareElement.style.display = 'none';
      }
    }
  }

  updateNavigationAvailability(slide, variant) {
    // Se a variante não está disponível, desabilitar navegação
    if (!variant.available) {
      this.nextButton.disabled = true;
      this.addButton.disabled = true;
    } else {
      this.nextButton.disabled = false;
      this.addButton.disabled = false;
    }
  }

  updateSwatchAvailability(slide, product, selectedOptions) {
    const fieldsets = slide.querySelectorAll('.kit-variant-fieldset');
    
    fieldsets.forEach((fieldset, index) => {
      // Pular a primeira opção (geralmente cor)
      if (index === 0 && fieldsets.length > 1) return;

      const radioButtons = fieldset.querySelectorAll('input[type="radio"]');
      const previousOption = index > 0 ? selectedOptions[index - 1] : null;

      // Filtrar variantes relevantes
      const relevantVariants = previousOption
        ? product.variants.filter((v) => v.options[index - 1] === previousOption)
        : product.variants;

      radioButtons.forEach((radio) => {
        const label = radio.nextElementSibling;
        const value = radio.value;
        
        // Verificar se esta opção está disponível
        const isAvailable = relevantVariants.some(
          (v) => v.options[index] === value && v.available
        );

        if (isAvailable) {
          radio.disabled = false;
          label.classList.add('available');
          label.classList.remove('soldout');
        } else {
          radio.disabled = true;
          label.classList.add('soldout');
          label.classList.remove('available');
        }
      });
    });
  }

  showSlide(index) {
    // Esconder todos os slides visíveis
    this.slides.forEach((slide) => {
      slide.style.display = 'none';
    });

    // Mostrar slide atual
    if (this.slides[index]) {
      this.slides[index].style.display = 'block';
      this.currentSlide = index;
      
      // Atualizar contador
      if (this.currentCounter) {
        this.currentCounter.textContent = index + 1;
      }
      
      this.updateNavigation();
      
      console.log(`📍 Navegando para slide ${index + 1} de ${this.slides.length}`);
      
      // Atualizar galeria com as imagens deste produto
      this.updateGalleryForCurrentSlide(this.slides[index]);
    }
  }

  /**
   * Atualiza galeria para o slide atual
   * @param {HTMLElement} slide - Slide atual
   */
  updateGalleryForCurrentSlide(slide) {
    const productId = parseInt(slide.dataset.productId, 10);
    const productData = this.config.products.find((p) => p.id === productId);
    if (!productData) return;

    let selectedColor = '';
    slide.querySelectorAll('.kit-variant-fieldset').forEach((fs) => {
      const legend = fs.querySelector('.kit-variant-legend');
      const name = (legend?.childNodes?.[0]?.textContent || legend?.textContent || '').toLowerCase();
      if (name.includes('cor') || name.includes('color')) {
        selectedColor = fs.querySelector('.current-value')?.dataset?.currentValue || '';
      }
    });
    if (!selectedColor) {
      const values = Array.from(slide.querySelectorAll('.current-value')).map(
        (el) => el.dataset.currentValue
      );
      selectedColor = values[0] || '';
    }

    this.updateGalleryWithProductImages(productData.handle, selectedColor);
  }

  previousSlide() {
    if (this.currentSlide > 0) {
      this.showSlide(this.currentSlide - 1);
    }
  }

  nextSlide() {
    if (this.currentSlide < this.slides.length - 1) {
      this.showSlide(this.currentSlide + 1);
    }
  }

  updateNavigation() {
    // Desabilitar botão anterior no primeiro slide
    this.prevButton.disabled = this.currentSlide === 0;

    // No último slide, mostrar botão de adicionar ao carrinho
    const isLastSlide = this.currentSlide === this.slides.length - 1;
    
    if (isLastSlide) {
      this.nextButton.style.display = 'none';
      this.addButton.style.display = 'flex';
    } else {
      this.nextButton.style.display = 'block';
      this.addButton.style.display = 'none';
    }
  }

  handleKeyboard(event) {
    switch (event.key) {
      case 'ArrowLeft':
        if (this.currentSlide > 0) {
          event.preventDefault();
          this.previousSlide();
        }
        break;
      case 'ArrowRight':
        if (this.currentSlide < this.slides.length - 1) {
          event.preventDefault();
          this.nextSlide();
        }
        break;
    }
  }

  async addToCart() {
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    const visibleSlides = this.getVisibleSlides();
    const items = [];
    const kitId = `kit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let kitLine = 0;

    visibleSlides.forEach((slide) => {
      const productId = parseInt(slide.dataset.productId);
      const rep = parseInt(slide.dataset.rep || '1');
      const key = `${productId}-${rep}`;
      const selection = this.selections.get(key);
      if (selection && selection.variantId) {
        kitLine += 1;
        items.push({
          id: selection.variantId,
          quantity: 1,
          properties: {
            _kit_id: kitId,
            _kit_line: String(kitLine),
            _kit_label: 'Conjunto',
          },
        });
      }
    });

    if (!items.length) {
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
      return;
    }

    const cartAddUrl =
      (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.cart_add_url) ||
      `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items }),
      });

      const responseJson = await response.json().catch(() => ({}));

      if (response.ok && !responseJson.status) {
        const cartType = window.Theme?.cart?.type || 'drawer';
        if (cartType === 'page') {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          return;
        }

        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: responseJson,
              sourceId: this.container.dataset.kitId || '',
              data: {
                source: 'product-kit-builder',
                itemCount: responseJson.item_count,
                items: responseJson.items,
              },
            },
          })
        );
      } else {
        const message = responseJson.description || responseJson.message || 'Erro ao adicionar ao carrinho.';
        alert(message);
      }
    } catch (error) {
      console.error('Kit Builder addToCart:', error);
      alert('Erro ao adicionar produtos ao carrinho. Por favor, tente novamente.');
    } finally {
      this.addButton.disabled = false;
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Sync galeria Horizon: se for o mesmo produto da PDP, usa filtro data-media-color
   * (mesmo sistema de filter_images_by_color). Se for outro produto do kit, busca
   * imagens via /products/{handle}.js e aplica nos slides sem destruir media-gallery.
   */
  async updateGalleryWithProductImages(productHandle, colorValue) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery || !productHandle) return;

    const pageHandle = this.config.pageProductHandle;
    const sameProduct = !pageHandle || pageHandle === productHandle;

    if (sameProduct) {
      this.restoreOriginalGalleryIfNeeded(gallery);
      this.filterMainGalleryByColor(colorValue);
      return;
    }

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}products/${productHandle}.js`);
      if (!response.ok) return;
      const productData = await response.json();
      const images = productData.images || [];
      this.applyExternalProductImages(gallery, images, colorValue);
    } catch (error) {
      console.error('Kit Builder gallery sync:', error);
    }
  }

  cacheOriginalGallery(gallery) {
    if (!gallery.dataset.kitGalleryCached) {
      gallery.dataset.kitGalleryCached = 'true';
      this._originalGalleryHTML = gallery.innerHTML;
    }
  }

  restoreOriginalGalleryIfNeeded(gallery) {
    if (gallery.dataset.kitExternalProduct === 'true' && this._originalGalleryHTML) {
      gallery.innerHTML = this._originalGalleryHTML;
      gallery.dataset.kitExternalProduct = 'false';
    }
  }

  /**
   * Normalização alinhada a media-gallery.js (#filterImagesByColor) do FIT LI
   */
  normalizeColorText(text, { forFilename = false } = {}) {
    let value = (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\.(jpe?g|png|webp|gif|avif)\b/gi, '');
    if (forFilename) {
      value = value.replace(/[/\\]+/g, ' ');
    }
    return value
      .replace(/[-_.]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  getFilenameFromSrc(src) {
    return (src || '').split('?')[0].split('/').pop() || '';
  }

  matchesColorNeedle(text, selectedColor, forFilename) {
    if (!selectedColor) return true;
    const needle = ` ${selectedColor} `;
    const hay = ` ${this.normalizeColorText(text, { forFilename })} `;
    return hay.includes(needle);
  }

  setMediaVisible(element, visible) {
    element.style.display = visible ? '' : 'none';
    if (visible) {
      element.removeAttribute('hidden');
    } else {
      element.setAttribute('hidden', 'true');
    }
    if (element instanceof HTMLElement) element.hidden = !visible;
  }

  getGalleryMediaElements(gallery) {
    return gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color], slideshow-slide, .carousel-extended__slide, .product-media-container'
    );
  }

  applyExternalProductImages(gallery, images, colorValue) {
    this.cacheOriginalGallery(gallery);
    gallery.dataset.kitExternalProduct = 'true';

    const useFilename = gallery.dataset.filterColorSource === 'filename';
    const selectedColor = colorValue
      ? this.normalizeColorText(colorValue, { forFilename: useFilename })
      : '';

    let imagesToShow = images;
    if (selectedColor) {
      const filtered = images.filter((img) => {
        const src = typeof img === 'string' ? img : img.src || '';
        const alt = typeof img === 'string' ? '' : img.alt || '';
        const text = useFilename ? this.getFilenameFromSrc(src) : alt || this.getFilenameFromSrc(src);
        return this.matchesColorNeedle(text, selectedColor, useFilename);
      });
      if (filtered.length) imagesToShow = filtered;
    }

    const slides = Array.from(
      gallery.querySelectorAll('slideshow-slide, .carousel-extended__slide')
    );
    if (!slides.length) return;

    slides.forEach((slide, index) => {
      const imgData = imagesToShow[index];
      const imgEl = slide.querySelector('img.product-media__image, img');
      if (!imgData || !imgEl) {
        this.setMediaVisible(slide, false);
        return;
      }

      const src = typeof imgData === 'string' ? imgData : imgData.src;
      const alt = typeof imgData === 'string' ? '' : imgData.alt || '';
      imgEl.src = src;
      if (imgEl.srcset) imgEl.removeAttribute('srcset');
      imgEl.alt = alt;

      const colorText = useFilename ? this.getFilenameFromSrc(src) : alt || this.getFilenameFromSrc(src);
      slide.setAttribute('data-media-color', colorText);
      this.setMediaVisible(slide, true);
    });

    // Thumbnails
    const thumbs = gallery.querySelectorAll('.slideshow-controls__thumbnails button, .slideshow-control');
    thumbs.forEach((thumb, index) => {
      const imgData = imagesToShow[index];
      const thumbImg = thumb.querySelector('img');
      if (!imgData) {
        this.setMediaVisible(thumb, false);
        return;
      }
      const src = typeof imgData === 'string' ? imgData : imgData.src;
      if (thumbImg) {
        thumbImg.src = src;
        if (thumbImg.srcset) thumbImg.removeAttribute('srcset');
      }
      this.setMediaVisible(thumb, true);
    });
  }

  /**
   * Filtra galeria da PDP por cor usando data-media-color (mesmo contrato do filtro nativo).
   */
  filterMainGalleryByColor(colorValue) {
    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    const filterByColor = gallery.dataset.filterByColor === 'true';
    if (!filterByColor && !colorValue) return;

    const useFilename = gallery.dataset.filterColorSource === 'filename';
    const selectedColor = colorValue
      ? this.normalizeColorText(colorValue, { forFilename: useFilename })
      : '';

    if (!selectedColor) return;

    const mediaElements = gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color]'
    );

    if (!mediaElements.length) {
      // Fallback: slides sem data-media-color — filtra por alt/filename da img
      const slides = gallery.querySelectorAll('slideshow-slide, .carousel-extended__slide');
      let matchCount = 0;
      slides.forEach((slide) => {
        const img = slide.querySelector('img');
        if (!img) return;
        const text = useFilename
          ? this.getFilenameFromSrc(img.getAttribute('src'))
          : img.getAttribute('alt') || this.getFilenameFromSrc(img.getAttribute('src'));
        const match = this.matchesColorNeedle(text, selectedColor, useFilename);
        this.setMediaVisible(slide, match);
        if (match) matchCount += 1;
      });
      if (matchCount === 0) {
        slides.forEach((slide) => this.setMediaVisible(slide, true));
      }
      return;
    }

    let matchCount = 0;
    mediaElements.forEach((el) => {
      const match = this.matchesColorNeedle(el.dataset.mediaColor || '', selectedColor, useFilename);
      this.setMediaVisible(el, match);
      if (match) matchCount += 1;
    });

    if (matchCount === 0) {
      mediaElements.forEach((el) => this.setMediaVisible(el, true));
    }

    // Thumbnails com data-media-color ou img
    const thumbs = gallery.querySelectorAll(
      '.slideshow-controls__thumbnails button, .slideshow-control, [data-thumbnail]'
    );
    thumbs.forEach((thumb) => {
      const colorAttr = thumb.dataset.mediaColor;
      if (colorAttr != null) {
        this.setMediaVisible(thumb, this.matchesColorNeedle(colorAttr, selectedColor, useFilename));
        return;
      }
      const thumbImg = thumb.querySelector('img');
      if (!thumbImg) return;
      const thumbAlt = thumbImg.getAttribute('alt') || '';
      let text = thumbAlt || this.getFilenameFromSrc(thumbImg.getAttribute('src'));
      if (useFilename) {
        text = this.getFilenameFromSrc(thumbImg.getAttribute('src'));
        // Vídeo/3D: a Shopify não expõe o nome original do arquivo; paridade com snippets/media-color-source.liquid.
        if (thumb.querySelector('.slideshow-controls__thumbnail-badge')) text += ` ${thumbAlt}`;
      }
      this.setMediaVisible(thumb, this.matchesColorNeedle(text, selectedColor, useFilename));
    });
  }

  normalizeString(str) {
    if (!str) return '';
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  formatMoney(cents) {
    const format =
      this.config.moneyFormat ||
      window.Theme?.moneyFormat ||
      window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, format || '{{amount}}');
    }
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-kit-id]').forEach((container) => {
    const kitId = container.dataset.kitId;
    const config = window.ProductKitBuilder?.[kitId];
    if (config) new ProductKitBuilder(container, config);
  });
});

